import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FaMapMarkerAlt, FaPhone, FaClock, FaMoneyBillWave, FaInfoCircle } from 'react-icons/fa';
import styled from 'styled-components';
import { getPoiDetails } from '../data/poiDatabase';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoidHJhdmVscG9ydHVnYWxjbHViIiwiYSI6ImNtOTZ6czVtNjAxcTMybXNpbXJnNGxwZXcifQ.lLXCMGzteF-T-x3aPGB3nw';

// Styled components
const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  position: relative;
`;

const InfoWindow = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 320px;
  max-width: 90%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 10;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
`;

const InfoHeader = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const InfoTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  color: #333;
`;

const InfoSubtitle = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
`;

const InfoImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

const InfoContent = styled.div`
  padding: 16px;
`;

const InfoSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoSectionTitle = styled.h4`
  display: flex;
  align-items: center;
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #495057;
  
  svg {
    margin-right: 8px;
    color: #6c757d;
  }
`;

const InfoText = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #495057;
  white-space: pre-line;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  
  &:hover {
    background: #e9ecef;
  }
`;

const MapComponent = ({
  items = [],
  center = [-9.1393, 38.7223],
  zoom = 12,
  style = {},
  onMarkerClick = () => {},
  showDetailedInfo = true,
  showImages = true,
  maxImageHeight = 120
}) => {
  // Refs
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  
  // State
  const [selectedPoi, setSelectedPoi] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Clear all markers
  const clearMarkers = useCallback(() => {
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
  }, []);

  // Format hours for display
  const formatHours = (hours) => {
    if (!hours) return 'Hours not available';
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    // Group consecutive days with same hours
    const groups = [];
    let currentGroup = null;
    
    days.forEach((day, index) => {
      if (hours[day]) {
        if (!currentGroup || currentGroup.hours !== hours[day]) {
          currentGroup = {
            startDay: index,
            endDay: index,
            hours: hours[day],
            days: [index]
          };
          groups.push(currentGroup);
        } else {
          currentGroup.endDay = index;
          currentGroup.days.push(index);
        }
      }
    });
    
    // Format groups
    return groups.map(group => {
      const startDay = dayNames[group.startDay];
      const endDay = dayNames[group.endDay];
      const dayRange = startDay === endDay ? startDay : `${startDay}-${endDay}`;
      return `${dayRange}: ${group.hours}`;
    }).join('\n');
  };

  // Format price information
  const formatPrice = (price) => {
    if (!price) return 'Price information not available';
    
    const parts = [];
    if (price.adult) parts.push(`Adult: ${price.currency || '€'}${price.adult}`);
    if (price.child) parts.push(`Child: ${price.currency || '€'}${price.child}`);
    if (price.senior) parts.push(`Senior: ${price.currency || '€'}${price.senior}`);
    if (price.student) parts.push(`Student: ${price.currency || '€'}${price.student}`);
    if (price.family) parts.push(`Family: ${price.currency || '€'}${price.family}`);
    
    return parts.length > 0 ? parts.join(' • ') : 'Free entry';
  };

  // Handle marker click
  const handleMarkerClick = useCallback((item) => {
    if (onMarkerClick) onMarkerClick(item);
    
    // Get detailed POI information
    const poiDetails = item.id ? getPoiDetails(item.id) : item;
    setSelectedPoi(poiDetails);
    
    // Center map on marker
    if (map.current && item.lat && item.lng) {
      map.current.flyTo({
        center: [item.lng, item.lat],
        zoom: 14,
        essential: true
      });
    }
  }, [onMarkerClick]);

  // Update markers on the map
  const updateMarkers = useCallback(() => {
    if (!map.current) return;
    
    clearMarkers();
    
    items.forEach((item) => {
      if (!item.lat || !item.lng) return;
      
      const el = document.createElement('div');
      el.className = 'marker';
      el.innerHTML = '<div style="background-color: #3b82f6; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; cursor: pointer;"></div>';
      
      const marker = new mapboxgl.Marker({
        element: el
      })
        .setLngLat([item.lng, item.lat])
        .addTo(map.current);
      
      // Add click handler
      el.addEventListener('click', () => {
        if (onMarkerClick) onMarkerClick(item);
        setSelectedPoi(item);
        
        // Center map on marker
        map.current.flyTo({
          center: [item.lng, item.lat],
          zoom: 14,
          essential: true
        });
      });
      
      markers.current.push(marker);
    });
    
    // Fit bounds to show all markers if there are any
    if (items.length > 0 && markers.current.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      items.forEach(item => {
        if (item.lat && item.lng) {
          bounds.extend([item.lng, item.lat]);
        }
      });
      
      map.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000
      });
    }
  }, [items, clearMarkers, onMarkerClick]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;
    
    // Clear existing map if it exists
    if (map.current) {
      clearMarkers();
      map.current.remove();
      map.current = null;
    }

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: center,
        zoom: zoom,
        maxZoom: 18,
        minZoom: 5
      });

      map.current.on('load', () => {
        setIsLoading(false);
        updateMarkers();
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e.error);
        setError('Failed to load map. Please try again later.');
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map. Please check your Mapbox token.');
      setIsLoading(false);
    }

    return () => {
      if (map.current) {
        clearMarkers();
        map.current.remove();
        map.current = null;
      }
    };
  }, [center, zoom, updateMarkers, clearMarkers]);

  // Update markers when items change
  useEffect(() => {
    if (map.current) {
      if (map.current.isStyleLoaded()) {
        updateMarkers();
      } else {
        // If style isn't loaded yet, wait for it
        const onStyleLoad = () => {
          updateMarkers();
          map.current.off('load', onStyleLoad);
        };
        map.current.on('load', onStyleLoad);
      }
    }
    
    // Cleanup function
    return () => {
      if (map.current) {
        map.current.off('load');
      }
    };
  }, [items, updateMarkers]);

  // Render the component
  return (
    <MapContainer ref={mapContainer} style={{
      ...style,
      position: 'relative'
    }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000,
          fontSize: '1.2rem',
          color: '#3b82f6'
        }}>
          Loading map...
        </div>
      )}
      
      {error && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          right: '10px',
          padding: '10px',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000,
          color: 'red',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {selectedPoi && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            maxWidth: '350px',
            maxHeight: '90vh',
            overflowY: 'auto',
            zIndex: 1000,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {/* POI Image */}
          {selectedPoi.images?.[0] && (
            <div style={{
              width: '100%',
              height: '180px',
              backgroundImage: `url(${selectedPoi.images[0].startsWith('@') ? selectedPoi.images[0].substring(1) : selectedPoi.images[0]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              position: 'relative'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPoi(null);
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: 0
                }}
              >
                ×
              </button>
            </div>
          )}
          
          {/* POI Content */}
          <div style={{ padding: '16px' }}>
            {/* Title and Category */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ 
                margin: '0 0 4px 0', 
                color: '#1a365d',
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '1.3'
              }}>
                {selectedPoi.title || 'Untitled Location'}
              </h3>
              {selectedPoi.category && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '4px'
                }}>
                  {selectedPoi.category.map((cat, i) => (
                    <span key={i} style={{
                      backgroundColor: '#e2e8f0',
                      color: '#4a5568',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {cat}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Description */}
            {selectedPoi.description && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ 
                  margin: '0 0 8px 0',
                  color: '#4a5568',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {selectedPoi.description}
                </p>
              </div>
            )}
            
            {/* Contact Information */}
            {(selectedPoi.phone || selectedPoi.email || selectedPoi.website) && (
              <div style={{ marginBottom: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: '#2d3748',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FaPhone style={{ color: '#718096' }} />
                  Contact Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedPoi.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#4a5568', fontSize: '14px' }}>📞</span>
                      <a 
                        href={`tel:${selectedPoi.phone}`}
                        style={{
                          color: '#3182ce',
                          textDecoration: 'none',
                          fontSize: '14px'
                        }}
                      >
                        {selectedPoi.phone}
                      </a>
                    </div>
                  )}
                  {selectedPoi.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#4a5568', fontSize: '14px' }}>✉️</span>
                      <a 
                        href={`mailto:${selectedPoi.email}`}
                        style={{
                          color: '#3182ce',
                          textDecoration: 'none',
                          fontSize: '14px',
                          wordBreak: 'break-all'
                        }}
                      >
                        {selectedPoi.email}
                      </a>
                    </div>
                  )}
                  {selectedPoi.website && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#4a5568', fontSize: '14px' }}>🌐</span>
                      <a 
                        href={selectedPoi.website.startsWith('http') ? selectedPoi.website : `https://${selectedPoi.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#3182ce',
                          textDecoration: 'none',
                          fontSize: '14px',
                          wordBreak: 'break-all'
                        }}
                      >
                        {selectedPoi.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Opening Hours */}
            {selectedPoi.hours && (
              <div style={{ marginBottom: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: '#2d3748',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FaClock style={{ color: '#718096' }} />
                  Opening Hours
                </h4>
                <div style={{ 
                  backgroundColor: '#f8fafc', 
                  padding: '8px 12px', 
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-line'
                }}>
                  {formatHours(selectedPoi.hours)}
                </div>
              </div>
            )}
            
            {/* Pricing */}
            {selectedPoi.price && (
              <div style={{ marginBottom: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: '#2d3748',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FaMoneyBillWave style={{ color: '#718096' }} />
                  Pricing
                </h4>
                <div style={{ 
                  backgroundColor: '#f8fafc', 
                  padding: '8px 12px', 
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  {formatPrice(selectedPoi.price)}
                </div>
              </div>
            )}
            
            {/* Tips */}
            {selectedPoi.tips?.length > 0 && (
              <div style={{ marginBottom: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: '#2d3748',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FaInfoCircle style={{ color: '#718096' }} />
                  Tips & Recommendations
                </h4>
                <ul style={{
                  margin: '0',
                  paddingLeft: '20px',
                  listStyleType: 'disc'
                }}>
                  {selectedPoi.tips.map((tip, index) => (
                    <li key={index} style={{
                      marginBottom: '6px',
                      fontSize: '13px',
                      color: '#4a5568',
                      lineHeight: '1.5'
                    }}>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Address */}
            {selectedPoi.address && (
              <div style={{ marginBottom: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: '#2d3748',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  📍 Address
                </h4>
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  {selectedPoi.address}
                </div>
              </div>
            )}
          </div>
          
          {/* Map Link */}
          {selectedPoi.lat && selectedPoi.lng && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #edf2f7',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${selectedPoi.lat},${selectedPoi.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#3182ce',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: '100%',
                  justifyContent: 'center'
                }}
              >
                📍 Get Directions
              </a>
            </div>
          )}
          <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
            <button
              onClick={() => setSelectedPoi(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#718096',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                marginLeft: '10px',
                lineHeight: 1
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {selectedPoi.categories && (
            <div style={{ marginBottom: '12px' }}>
              {selectedPoi.categories.map((category, i) => (
                <span 
                  key={i}
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#ebf8ff',
                    color: '#2b6cb0',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginRight: '6px',
                    marginBottom: '6px',
                    fontWeight: '500'
                  }}
                >
                  {category}
                </span>
              ))}
            </div>
          )}

          {selectedPoi.desc && (
            <p style={{ 
              margin: '0 0 16px 0', 
              fontSize: '14px', 
              color: '#4a5568',
              lineHeight: '1.5'
            }}>
              {selectedPoi.desc.length > 200 
                ? `${selectedPoi.desc.substring(0, 200)}...` 
                : selectedPoi.desc}
            </p>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#4a5568'
          }}>
            {selectedPoi.rating !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '6px', color: '#ecc94b' }}>★</span>
                <span>{selectedPoi.rating} ({selectedPoi.ratingsTotal || 0} reviews)</span>
              </div>
            )}

            {selectedPoi.priceLevel !== undefined && (
              <div>
                <span>Price: </span>
                <span style={{ color: '#2f855a', fontWeight: '500' }}>
                  {Array(selectedPoi.priceLevel).fill('€').join('')}
                  {selectedPoi.priceLevel < 4 ? Array(4 - selectedPoi.priceLevel).fill('€').map((_, i) => (
                    <span key={i} style={{ opacity: 0.3 }}>€</span>
                  )) : null}
                </span>
              </div>
            )}

            {selectedPoi.openingHours && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', marginBottom: '4px' }}>
                  <span style={{ minWidth: '80px', fontWeight: '500' }}>Hours:</span>
                  <span style={{ color: selectedPoi.openingHours.openNow ? '#38a169' : '#e53e3e' }}>
                    {selectedPoi.openingHours.openNow ? 'Open Now' : 'Closed'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '10px',
            backgroundColor: '#f7fafc',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#4a5568'
          }}>
            <FaMapMarkerAlt style={{ color: '#e53e3e', marginRight: '8px', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: '500', marginBottom: '2px' }}>{selectedPoi.address || 'Address not available'}</div>
              <div style={{ fontSize: '12px', color: '#718096' }}>
                {selectedPoi.lat?.toFixed(4)}, {selectedPoi.lng?.toFixed(4)}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${selectedPoi.lat},${selectedPoi.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: '#f7fafc',
                color: '#2d3748',
                border: '1px solid #e2e8f0',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                textAlign: 'center',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span>Directions</span>
              <span>↗</span>
            </a>
            <button
              onClick={() => setSelectedPoi(null)}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </MapContainer>
  );
};

MapComponent.propTypes = {
  items: PropTypes.array,
  center: PropTypes.array,
  zoom: PropTypes.number,
  style: PropTypes.object,
  onMarkerClick: PropTypes.func,
  showDetailedInfo: PropTypes.bool,
  showImages: PropTypes.bool,
  maxImageHeight: PropTypes.number
};

export default MapComponent;
